export type AwakeState = 'off' | 'paused' | 'requesting' | 'active' | 'unavailable' | 'released';

/** Owns one screen lease, including grants arriving after the game was suspended. */
export class ScreenAwake {
  state: AwakeState = 'off';
  private desired = false;
  private generation = 0;
  private attempted = -1;
  private pending?: Promise<void>;
  private lease?: WakeLockSentinel;

  constructor(private request: (() => Promise<WakeLockSentinel>) | undefined,
    private changed: () => void = () => {}) {}

  private publish(state: AwakeState): void {
    if (state !== this.state) { this.state = state; this.changed(); }
  }

  update(enabled: boolean, eligible: boolean): Promise<void> {
    const desired = enabled && eligible;
    if (desired !== this.desired) { this.desired = desired; this.generation++; }
    if (!desired) {
      const lease = this.lease; this.lease = undefined;
      this.publish(enabled ? 'paused' : 'off');
      return lease ? lease.release().catch(() => {}) : Promise.resolve();
    }
    if (this.lease) { this.publish('active'); return Promise.resolve(); }
    if (this.pending) return this.pending;
    if (this.attempted === this.generation) return Promise.resolve();
    const generation = this.generation; this.attempted = generation;
    if (!this.request) { this.publish('unavailable'); return Promise.resolve(); }
    this.publish('requesting');
    this.pending = Promise.resolve().then(this.request).then(async lease => {
      if (!this.desired || generation !== this.generation) { await lease.release(); return; }
      this.lease = lease;
      lease.addEventListener('release', () => {
        if (this.lease === lease) { this.lease = undefined; this.publish('released'); }
      });
      this.publish('active');
    }).catch(() => {
      if (this.desired && generation === this.generation) this.publish('unavailable');
    }).finally(() => {
      this.pending = undefined;
      if (this.desired && this.attempted !== this.generation) void this.update(true, true);
    });
    return this.pending;
  }
}
